import React from 'react';
import { createRoot } from 'react-dom/client';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import 'bootstrap/dist/css/bootstrap.min.css'; // Import Bootstrap CSS
import './styles/girvi-screen.css';

import App from './App';

const theme = createTheme({
  palette: {
    primary: { main: '#0b1220' },
    secondary: { main: '#c9a24d' },
    background: { default: '#f3f5f8', paper: '#ffffff' },
    text: { primary: '#0f172a', secondary: '#64748b' },
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif'
    ].join(','),
    button: { textTransform: 'none', fontWeight: 700 },
    h6: { fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em' },
  },
  components: {
    MuiTextField: { defaultProps: { size: 'small' } },
    MuiFormControl: { defaultProps: { size: 'small' } },
    MuiSelect: { defaultProps: { size: 'small' } },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 10, fontWeight: 700, boxShadow: 'none' },
        containedPrimary: {
          background: 'linear-gradient(180deg, #1e293b 0%, #0b1220 100%)',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#fff',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#c9a24d' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#c9a24d', borderWidth: 1.5 },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontWeight: 700, '&.Mui-focused': { color: '#8a6d2f' } },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#f8fafc',
            color: '#64748b',
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            borderBottom: '2px solid #e7d3a1',
            whiteSpace: 'nowrap',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: { root: { fontSize: 13, borderColor: '#e2e8f0', padding: '7px 10px' } },
    },
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 700 } },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          border: '1px solid rgba(212,175,106,0.35)',
          boxShadow: '0 28px 80px rgba(8,15,28,0.45)',
          overflow: 'hidden',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(115deg, #0b1220 0%, #16233a 55%, #1e3a5f 100%)',
          color: '#f8fafc',
          fontWeight: 800,
          fontSize: 18,
          letterSpacing: '-0.02em',
          padding: '14px 20px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          backgroundColor: '#f4f7fb',
          padding: '16px 18px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
          borderTop: '1px solid #e2e8f0',
          padding: '10px 16px',
          gap: 8,
        },
      },
    },
  },
});

const root = createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App />
    </ThemeProvider>
  </React.StrictMode>
);